import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { storeVenderData } from 'store/slices/venderslice';
import { getProfile } from 'services/vendorAPI';
import axios from 'axios';
import SVG from 'components/renderSvg';
import escape from 'media/svgs/escape.svg';
import logo from 'media/pngs/logo.png';
import toast from 'react-hot-toast';

const SubscriptionSuccess = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSuccessData = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor/payment_success/${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                setData(response.data);

                // Fetch updated profile and update Redux store
                const profileResponse = await getProfile();
                if (profileResponse.success) {
                    dispatch(storeVenderData(profileResponse.data));
                }
            } catch (error) {
                console.error('Error fetching success data:', error);
                toast.error('Failed to retrieve subscription details.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchSuccessData();
        }
    }, [id]);

    if (loading) {
        return (
            <div className='w-full min-h-screen flex flex-col items-center justify-center bg-[#fafafa]'>
                <div className='animate-bounce mb-4'>
                    <img src={logo} alt='logo' className='w-16 h-auto opacity-50' />
                </div>
                <div className='flex flex-col items-center gap-2'>
                    <div className='w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
                    <p className='text-gray-500 font-bold uppercase tracking-widest text-xs'>Verifying Subscription...</p>
                </div>
            </div>
        );
    }

    return (
        <div className='w-full min-h-screen bg-[#fafafa] flex flex-col items-center justify-center p-4 relative overflow-hidden font-sans'>
            {/* Decorative background elements */}
            <div className='absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full -z-10 animate-pulse'></div>
            <div className='absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-indigo-600/10 blur-[120px] rounded-full -z-10 animate-pulse delay-700'></div>

            <div className='max-w-xl w-full bg-white rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden border border-gray-100 transform transition-all hover:scale-[1.01]'>
                <div className='bg-gradient-to-br from-primary via-indigo-600 to-purple-600 p-12 text-center text-white relative'>
                    {/* Sparkles effect */}
                    <div className='absolute top-6 right-8 text-2xl animate-bounce'>✨</div>
                    <div className='absolute top-12 left-10 text-xl animate-bounce delay-300'>✨</div>
                    <div className='absolute bottom-6 left-12 text-2xl animate-bounce delay-700'>✨</div>

                    <div className='w-24 h-24 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 border border-white/30 shadow-lg animate-[wiggle_1s_ease-in-out_infinite]'>
                        <svg className='w-12 h-12 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='3' d='M5 13l4 4L19 7' />
                        </svg>
                    </div>
                    <h1 className='text-4xl font-black mb-2 tracking-tighter uppercase'>Payment Success!</h1>
                    <p className='text-white/80 font-bold text-lg'>Welcome to <span className='text-white underline decoration-wavy decoration-indigo-300'>Pro Membership</span></p>
                </div>

                <div className='p-10 space-y-8'>
                    <div className='grid grid-cols-1 gap-4'>
                        <div className='flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100 group transition-all hover:bg-white hover:border-primary/20 hover:shadow-md'>
                            <span className='text-xs text-gray-400 font-black uppercase tracking-widest'>Email Address</span>
                            <p className='text-dark font-black text-sm'>{data?.email || 'N/A'}</p>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <div className='p-5 bg-gray-50 rounded-2xl border border-gray-100 transition-all hover:bg-white hover:border-primary/20 hover:shadow-md'>
                                <span className='text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-2'>Plan Tier</span>
                                <span className='inline-flex items-center px-4 py-1.5 bg-gradient-to-r from-primary to-indigo-600 text-white text-[10px] font-black rounded-full uppercase tracking-widest shadow-md'>
                                    {data?.plan_tier || 'PRO'}
                                </span>
                            </div>
                            <div className='p-5 bg-gray-50 rounded-2xl border border-gray-100 transition-all hover:bg-white hover:border-primary/20 hover:shadow-md'>
                                <span className='text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-2'>Status</span>
                                <span className='inline-flex items-center px-4 py-1.5 bg-green-500 text-white text-[10px] font-black rounded-full uppercase tracking-widest shadow-md'>
                                    {data?.subscription_status || 'ACTIVE'}
                                </span>
                            </div>
                        </div>

                        <div className='p-5 bg-gray-50 rounded-2xl border border-gray-100 transition-all hover:bg-white hover:border-primary/20 hover:shadow-md'>
                            <span className='text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-2'>Subscription ID</span>
                            <p className='text-[10px] font-mono text-gray-500 break-all leading-relaxed'>{data?.subscription_id || 'N/A'}</p>
                        </div>
                    </div>

                    <div className='space-y-4 pt-2'>
                        <button
                            onClick={() => navigate('/')}
                            className='w-full py-4 bg-primary text-white font-black rounded-2xl shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 transition-all active:scale-95 text-sm uppercase tracking-widest'
                        >
                            Go to Dashboard
                        </button>
                        <p className='text-center text-[10px] text-gray-400 uppercase font-black tracking-[0.2em]'>
                            Sportona™ Premium Experience
                        </p>
                    </div>
                </div>
            </div>

            {/* Back to plans link */}
            <button
                className='mt-10 flex items-center space-x-2 px-8 py-3 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all hover:-translate-x-1 active:scale-95 group'
                onClick={() => navigate('/plans')}
            >
                <SVG icon={escape} className='size-5 text-gray-400 group-hover:text-primary transition-colors' />
                <span className='font-black text-gray-500 group-hover:text-primary text-[10px] uppercase tracking-widest'>View Membership Plans</span>
            </button>

            <style dangerouslySetInnerHTML={{
                __html: `
        @keyframes wiggle {
          0%, 100% { transform: rotate(-5deg) scale(1.1); }
          50% { transform: rotate(5deg) scale(1.1); }
        }
        .animate-success-pop {
          animation: pop 0.5s cubic-bezier(0.26, 0.53, 0.74, 1.48) forwards;
        }
        @keyframes pop {
          0% { opacity: 0; transform: scale(0.5); }
          100% { opacity: 1; transform: scale(1.1); }
        }
      `}} />
        </div>
    );
};

export default SubscriptionSuccess;
